import { call, put, select, takeEvery, race, delay } from 'redux-saga/effects';
import {
  CONCILIAR_TRANSACOES,
  CONCILIAR_TRANSACOES_PENDING,
  CONCILIAR_TRANSACOES_SUCCESS,
  CONCILIAR_TRANSACOES_FAILURE,
  NEW_FILTER_CONCILIACAO_BANCARIA,
  BUSCAR_RESUMO_CONCILIACAO
} from '../actionTypes';
import { conciliacaoBancariaService } from '../service';
import { AlertError, AlertSuccess } from '@components/common/AlertToast';

// Camada 2: Map para rastrear requisições em andamento (proteção contra double-submit)
const activeRequests = new Map(); // requestId -> { transactionId, startTime }

export function* conciliarTransacoes(action) {
  const { requestId, transactionId, contaBancoId, conciliacoes } = action;

  // PROTEÇÃO 1: Verificar duplicação por requestId
  if (activeRequests.has(requestId)) {
    console.warn(`[SAGA CONCILIAÇÃO] Requisição duplicada detectada: ${requestId}`);
    return; // Ignorar silenciosamente
  }

  // PROTEÇÃO 2: Verificar se mesma transação está sendo processada
  const existingRequest = Array.from(activeRequests.entries()).find(
    ([id, data]) => data.transactionId === transactionId
  );

  if (existingRequest) {
    console.warn(`[SAGA CONCILIAÇÃO] Transação ${transactionId} já está sendo processada (requestId: ${existingRequest[0]})`);
    return;
  }

  // Registrar requisição
  activeRequests.set(requestId, {
    transactionId,
    startTime: Date.now()
  });

  try {
    yield put({ type: CONCILIAR_TRANSACOES_PENDING });

    // Validações básicas
    if (!contaBancoId) {
      throw new Error('ContaBancoId é obrigatório');
    }

    if (!conciliacoes || conciliacoes.length === 0) {
      throw new Error('Pelo menos uma conciliação deve ser informada');
    }

    // Validar estrutura das conciliações
    for (let conciliacao of conciliacoes) {
      if (!conciliacao.lancamentoOFXId) {
        throw new Error('lancamentoOFXId é obrigatório para cada conciliação');
      }

      if (!conciliacao.registros || conciliacao.registros.length === 0) {
        throw new Error('registros é obrigatório para cada conciliação');
      }

      // Validar cada registro
      for (let registro of conciliacao.registros) {
        if (!registro.tipoAcao) {
          throw new Error('tipoAcao é obrigatório para cada registro');
        }

        // Validações específicas por tipo de ação
        if (registro.tipoAcao === 'VINCULADO') {
          if (!registro.tipoRegistro) {
            throw new Error('tipoRegistro é obrigatório para registros do tipo VINCULADO');
          }
          if (!registro.registroId) {
            throw new Error('registroId é obrigatório para registros do tipo VINCULADO');
          }
        }

        if (registro.tipoAcao === 'NOVO') {
          if (!registro.historico) {
            throw new Error('historico é obrigatório para novos registros');
          }
          if (!registro.contaGerencialId) {
            throw new Error('contaGerencialId é obrigatório para novos registros');
          }

          // Se gerar conta financeira, validar campos adicionais
          if (registro.gerarContaFinanceira) {
            if (!registro.dataVencimento) {
              throw new Error('dataVencimento é obrigatório quando gerarContaFinanceira = true');
            }
          }
        }
      }
    }

    const payload = {
      contaBancoId,
      conciliacoes
    };

    // PROTEÇÃO 3: Race condition com timeout de 30 segundos
    const { response, timeout } = yield race({
      response: call(conciliacaoBancariaService.salvar, payload),
      timeout: delay(30000) // 30 segundos
    });

    if (timeout) {
      throw new Error('Timeout: A operação demorou mais de 30 segundos');
    }

    yield put({
      type: CONCILIAR_TRANSACOES_SUCCESS,
      data: response.data,
      requestId,
      transactionId
    });

    // Mostrar mensagem de sucesso
    const totalConciliadas = conciliacoes.length;
    AlertSuccess(
      `${totalConciliadas} ${
        totalConciliadas === 1
          ? 'transação conciliada com sucesso!'
          : 'transações conciliadas com sucesso!'
      }`,
    );

    // Recarregar dados da listagem após sucesso, preservando o filtro de tipo (entradas/saídas)
    const currentFilter = yield select(state => state.entities.conciliacaoBancariaReducer.filter);
    yield put({
      type: NEW_FILTER_CONCILIACAO_BANCARIA,
      newFilter: true, // Usar true para preservar tipoTransacao
      args: {
        ...currentFilter,
        page: currentFilter.page || 0 // Manter na mesma página se possível
      }
    });

    // Atualizar resumo da conciliação
    yield put({
      type: BUSCAR_RESUMO_CONCILIACAO,
      contaBancoId
    });

  } catch (error) {
    yield put({
      type: CONCILIAR_TRANSACOES_FAILURE,
      error: error.message || 'Erro ao conciliar transações',
      requestId,
      transactionId
    });

    // Mostrar mensagem de erro
    const errorMessage = error.response?.data?.message || error.message || 'Erro ao conciliar transações';
    AlertError(errorMessage);
  } finally {
    // PROTEÇÃO 4: Limpar requisição do map
    activeRequests.delete(requestId);

    // PROTEÇÃO 5: Limpeza de segurança - remover requisições antigas (>5 minutos)
    const now = Date.now();
    for (const [id, data] of activeRequests.entries()) {
      if (now - data.startTime > 300000) { // 5 minutos
        console.warn(`[SAGA CONCILIAÇÃO] Limpando requisição antiga: ${id}`);
        activeRequests.delete(id);
      }
    }
  }
}

export function* watchConciliarTransacoes() {
  yield takeEvery(CONCILIAR_TRANSACOES, conciliarTransacoes);
}